import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../../../core/authentication/authentication.service';
import { LoggedUserService } from '../../../../core/services/logged-user.service';
import { UserService } from '../../../../core/http';
import { take } from 'rxjs/internal/operators/take';
import { InactivityService } from '../../../../core/services/inactivity.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserInfo } from '../../../../shared/models';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from '../../../../core/services/snack.service';
import { PaymentOverdueService } from '../../../../core/services/payment-overdue.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false,
})
export class LoginComponent {
  form!: FormGroup;
  passwordVisible = false;

  constructor(
    private readonly router: Router,
    private readonly authenticationService: AuthenticationService,
    private readonly loggedUserService: LoggedUserService,
    private readonly userService: UserService,
    private readonly loggerUserService: LoggedUserService,
    private readonly inactivityService: InactivityService,
    private formBuilder: FormBuilder,
    private readonly snackbarService: SnackbarService,
    private readonly paymentOverdueService: PaymentOverdueService,
  ) {
    this.form = this.formBuilder.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
    });
  }

  login() {
    const { username, password } = this.form.value;

    if (this.form.valid) {
      this.authenticationService
        .authenticate(username, password)
        .pipe(take(1))
        .subscribe({
          next: () => {
          this.userService
            .getLoggedUserInfo()
            .pipe(take(1))
            .subscribe((result) => {
              this.userService
                .getImpersonationUsers()
                .pipe(take(1))
                .subscribe({
                  next: (impersonationData: any) => {
                    if (result.user.linkerUserId !== result.user.innovaUserId) {
                      result.user.isImpersonated = true;
                      const originalUser = this.findUserById(
                        result.user.linkerUserId,
                        impersonationData.companys || [],
                      );
                      if (originalUser) {
                        result.user.impersonatedBy = originalUser.fullName;
                      }
                    }

                    this.completeLogin(result);
                  },
                  error: () => {
                    if (result.user.linkerUserId !== result.user.innovaUserId) {
                      result.user.isImpersonated = true;
                    }

                    this.completeLogin(result);
                  },
                });
            });
          },
          error: (error: HttpErrorResponse) => {
            this.snackbarService.showError(this.getLoginErrorMessage(error));
          },
        });
    }
  }

  private getLoginErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 401 || error.status === 400) {
      return 'Usuario o contraseña incorrectos. Inténtalo de nuevo.';
    }

    // The API returns ProblemDetails for known failures (e.g. auth/user-not-linked),
    // where `detail` is the actionable text meant for the user.
    const detail = error.error?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }

    return 'No pudimos iniciar sesión. Inténtalo de nuevo más tarde.';
  }

  private completeLogin(result: UserInfo) {
    this.loggerUserService.loggedUserInfo = result;
    this.loggedUserService.loggedUser = true;

    this.inactivityService.startInactivityService();

    // The shell is already mounted, so its ngOnInit will not run again: the
    // notice is triggered here with the info just loaded.
    this.paymentOverdueService.start(true);

    this.navigate();
  }

  navigate() {
    this.router.navigate(['/home']);
  }

  private findUserById(userId: string, companies: any[]): any {
    for (const company of companies) {
      if (company.users) {
        const user = company.users.find((user: any) => user.id === userId);
        if (user) {
          return user;
        }
      }
    }
    return null;
  }
}
